/**
 * Find Contact Handler
 * Query handler for finding contacts by ID or name using GraphQL
 */

import type { ToolExecutionContext } from '../../types/index.js';
import { QueryHandlerBase } from '../base/index.js';
import { stringField, uuidField } from '../../utils/schema-builder.js';
import { createUserGraphQLClient } from '../../utils/graphql-client.js';

/**
 * Find contact handler
 * Uses GraphQL queries to search for contacts with RLS enforcement
 */
export class FindContactHandler extends QueryHandlerBase<any> {
  id = 'find_contact';

  schema = {
    type: 'object',
    properties: {
      id: uuidField('UUID of the contact (optional if name is provided)'),
      name: stringField('Contact name to search for (optional if id is provided)'),
    },
    required: [],
  };

  async execute(params: Record<string, any>, context?: ToolExecutionContext): Promise<any> {
    if (!params.id && !params.name) {
      throw new Error('Either id or name must be provided');
    }

    if (!context?.userToken) {
      throw new Error('User token is required for contact search');
    }

    try {
      // Dynamically import GraphQL types and documents to avoid circular dependencies
      const { GetContactsDocument, GetContactssDocument } = await import('@/src/gql/graphql');
      
      // Create user-scoped GraphQL client that respects RLS
      const client = createUserGraphQLClient(context.userToken);

      if (params.id) {
        // Search by ID using GraphQL
        const result = await client.query({
          query: GetContactsDocument,
          variables: {
            id: params.id,
          },
          fetchPolicy: 'network-only',
        });

        if (result.error) {
          throw new Error(`GraphQL error: ${result.error.message}`);
        }

        const data = result.data as any;
        const contact = data?.contactsCollection?.edges?.[0]?.node;
        if (!contact) {
          return {
            error: `Contact with ID "${params.id}" not found`,
            id: params.id,
          };
        }

        return {
          ...contact,
          viewInApp: `[View Contact](/contacts/${contact.id})`,
        };
      } else {
        // Search by name using GraphQL with filter
        const result = await client.query({
          query: GetContactssDocument,
          variables: {
            first: 5,
            filter: {
              name: {
                ilike: `%${params.name}%`,
              },
            },
          },
          fetchPolicy: 'network-only',
        });

        if (result.error) {
          throw new Error(`GraphQL error: ${result.error.message}`);
        }

        const data = result.data as any;
        const contacts = data?.contactsCollection?.edges?.map((edge: any) => edge?.node).filter((node: any) => node != null) || [];

        if (contacts.length === 0) {
          return {
            error: `No contacts found matching "${params.name}"`,
            query: params.name,
          };
        }

        const viewInApp = (c: any) => `[${c.name || 'Contact'}](/contacts/${c.id})`;
        if (contacts.length === 1) {
          return { ...contacts[0], viewInApp: viewInApp(contacts[0]) };
        }
        return {
          matches: contacts.map((c: any) => ({ ...c, viewInApp: viewInApp(c) })),
          count: contacts.length,
          viewInApp: contacts.map(viewInApp).join(' · '),
        };
      }
    } catch (error) {
      throw new Error(`Contact search failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }
}
